import { ClarificationQuestion, ClarificationResponse } from '../components/ClarificationMessage';

// Timeline item types
export type TimelineItemType =
  | 'user_input'            // User's initial question
  | 'progress_block'        // Block of progress steps (collapsible)
  | 'clarification_request' // Questions from assistant
  | 'report'                // Final analysis report
  | 'followup_question'     // User follow-up question
  | 'followup_response';    // Assistant response to follow-up

// Progress step from SSE
export interface ProgressStep {
  type: string;
  message: string;
  timestamp: string;
  completed: boolean;
}

// Analysis phase
export type AnalysisPhase = 'clarification' | 'exploration' | 'analysis' | 'synthesis' | 'complete';

// Base timeline item
export interface BaseTimelineItem {
  id: string;
  type: TimelineItemType;
  createdAt: string;
  roundNumber: number;  // Which analysis round (1 = initial, 2+ = after clarification)
}

// User input item
export interface UserInputItem extends BaseTimelineItem {
  type: 'user_input';
  content: string;
}

// Progress block item - groups multiple progress steps
export interface ProgressBlockItem extends BaseTimelineItem {
  type: 'progress_block';
  steps: ProgressStep[];
  phase: AnalysisPhase;
  isComplete: boolean;
  isStreaming: boolean;
}

// Clarification request item
export interface ClarificationRequestItem extends BaseTimelineItem {
  type: 'clarification_request';
  requestId: string;
  questions: ClarificationQuestion[];
  status: 'pending' | 'answered';
  responses?: ClarificationResponse[];
}

// Report item
export interface ReportItem extends BaseTimelineItem {
  type: 'report';
  markdown: string;
}

// Followup question item
export interface FollowupQuestionItem extends BaseTimelineItem {
  type: 'followup_question';
  content: string;
}

// Followup response item
export interface FollowupResponseItem extends BaseTimelineItem {
  type: 'followup_response';
  content: string;
  referencedJudgments?: string[];
  tokensUsed?: number;
  isStreaming: boolean;
}

// Union type for all timeline items
export type ConversationTimelineItem =
  | UserInputItem
  | ProgressBlockItem
  | ClarificationRequestItem
  | ReportItem
  | FollowupQuestionItem
  | FollowupResponseItem;

// Session status
export type SessionStatus = 'idle' | 'analyzing' | 'awaiting_clarification' | 'complete' | 'error';

// Conversation session
export interface ConversationSession {
  id: string | null;
  status: SessionStatus;
  timeline: ConversationTimelineItem[];
  currentRound: number;
  tokensUsed: number;
  tokensLimit: number;
  error: string | null;
}

// Type guards for timeline items
export function isUserInputItem(item: ConversationTimelineItem): item is UserInputItem {
  return item.type === 'user_input';
}

export function isProgressBlockItem(item: ConversationTimelineItem): item is ProgressBlockItem {
  return item.type === 'progress_block';
}

export function isClarificationRequestItem(item: ConversationTimelineItem): item is ClarificationRequestItem {
  return item.type === 'clarification_request';
}

export function isReportItem(item: ConversationTimelineItem): item is ReportItem {
  return item.type === 'report';
}

export function isFollowupQuestionItem(item: ConversationTimelineItem): item is FollowupQuestionItem {
  return item.type === 'followup_question';
}

export function isFollowupResponseItem(item: ConversationTimelineItem): item is FollowupResponseItem {
  return item.type === 'followup_response';
}

// Helper to generate unique IDs
export function generateTimelineId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}
